import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';

interface AvatarVersionContextType {
  getAvatarUrl: (userId: string, baseUrl?: string) => string;
  invalidateUserAvatar: (userId: string) => void;
  getUserVersion: (userId: string) => number;
}

interface AvatarVersionState {
  [userId: string]: number;
}

const AvatarVersionContext = createContext<AvatarVersionContextType | undefined>(undefined);

export const AvatarVersionProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [avatarVersions, setAvatarVersions] = useState<AvatarVersionState>({});

  // Generate versioned avatar URL
  const getAvatarUrl = useCallback((userId: string, baseUrl?: string): string => {
    if (!baseUrl) return '';
    
    const version = avatarVersions[userId] || 1;
    const separator = baseUrl.includes('?') ? '&' : '?';
    return `${baseUrl}${separator}v=${version}`;
  }, [avatarVersions]);

  // Invalidate user avatar by incrementing version
  const invalidateUserAvatar = useCallback((userId: string) => {
    setAvatarVersions(prev => ({
      ...prev,
      [userId]: (prev[userId] || 1) + 1
    }));
  }, []);

  // Get current version for a user
  const getUserVersion = useCallback((userId: string): number => {
    return avatarVersions[userId] || 1;
  }, [avatarVersions]);

  // Listen for profileUpdated events and invalidate avatar cache
  useEffect(() => {
    const handleProfileUpdated = (event: CustomEvent) => {
      const { username, userId } = event.detail;
      
      // Invalidate cache for both username and userId (if available)
      // This ensures compatibility with components that use either identifier
      if (username) {
        invalidateUserAvatar(username);
      }
      if (userId) {
        invalidateUserAvatar(userId);
      }
    };

    // Add event listener for profileUpdated events
    window.addEventListener('profileUpdated', handleProfileUpdated as EventListener);

    return () => {
      window.removeEventListener('profileUpdated', handleProfileUpdated as EventListener);
    };
  }, [invalidateUserAvatar]);

  const value: AvatarVersionContextType = {
    getAvatarUrl,
    invalidateUserAvatar,
    getUserVersion
  };

  return (
    <AvatarVersionContext.Provider value={value}>
      {children}
    </AvatarVersionContext.Provider>
  );
};

export const useAvatarVersion = (): AvatarVersionContextType => {
  const context = useContext(AvatarVersionContext);
  if (context === undefined) {
    throw new Error('useAvatarVersion must be used within an AvatarVersionProvider');
  }
  return context;
};
